import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";

export default {
  name: "subjectImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },
  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      acdYearList: [],
      template_acd_year_id: null,
      acdYearId: null,
      file_name: "",
      invalidFileType: false,
      invalidFileSize: false,
      submitted: false,
      templateSubmitted: false,
      disableImportBtn: false,
      isCommitting: false,
      rowsNormalized: [],
      previewErrors: [],
    };
  },

  validations: {
    acdYearId: { required },
    file_name: { required },
  },

  computed: {
    disabledTemplateBtn() {
      return false;
    },

    previewRows() {
      return this.rowsNormalized;
    },

    disableCommitBtn() {
      return this.isCommitting || !this.previewRows.length;
    },

    previewVisible() {
      return this.previewErrors.length > 0 || this.rowsNormalized.length > 0;
    },

    canShowPreviewList() {
      return this.previewErrors.length === 0 && this.previewRows.length > 0;
    },

    selectedAcademicYearName() {
      const selected = this.acdYearList.find(
        (period) => String(period.id) === String(this.acdYearId)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },
  },

  methods: {
    async getParentAcdYearList() {
      try {
        this.isLoading = true;
        const response = await axios.get("period/periodList");
        if (response.data) {
          this.acdYearList = response.data.data || [];
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    chooseFile() {
      this.$refs.fileUploader.value = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.invalidFileSize = false;
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.invalidFileType = false;
      this.invalidFileSize = false;

      if (!file) return;

      const allowedTypes = [".xls", ".xlsx"];
      const ext = `.${file.name.split(".").pop().toLowerCase()}`;

      if (!allowedTypes.includes(ext)) {
        this.invalidFileType = true;
        this.file_name = "";
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.invalidFileSize = true;
        this.file_name = file.name;
        return;
      }

      this.file_name = file.name;
      this.clearPreview();
    },

    async templateDownload() {
      this.templateSubmitted = true;

      if (this.previewVisible) {
        return;
      }

      const params = {};
      if (this.template_acd_year_id) {
        params.academic_year_id = this.template_acd_year_id;
      }

      this.isLoading = true;
      try {
        const response = await axios.get("/subject/template-download", {
          params,
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const selectedYear = this.acdYearList.find(y => y.id == this.template_acd_year_id);
        const now = new Date();
        const dateStr = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
        const periodName = (selectedYear?.text || "all").replace(/\s+/g, "_");
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${periodName}_subjects_${dateStr}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.handleImportError(error, "Failed to download subject template.");
      } finally {
        this.isLoading = false;
      }
    },

    async confirmImport() {
      this.submitted = true;
      this.clearPreview();

      if (this.$v.$invalid) return;

      const file = this.$refs.fileUploader.files[0];
      if (!file) {
        this.invalidFileType = true;
        this.file_name = "";
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.invalidFileSize = true;
        return;
      }

      await this.previewImport(file);
    },

    async previewImport(file) {
      this.isLoading = true;
      this.disableImportBtn = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.acdYearId);
      formData.append("file", file);

      try {
        const response = await axios.post("/subject/import/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.applyPreviewResponse(response.data);
      } catch (error) {
        this.handleImportValidationError(error, "Subject import preview failed.");
      } finally {
        this.disableImportBtn = false;
        this.isLoading = false;
      }
    },

    async confirmSave() {
      if (this.disableCommitBtn) return;

      this.clearImportConfirmErrors();

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.isCommitting = true;

      try {
        const response = await axios.post("/subject/import/commit", {
          academic_year_id: this.acdYearId,
          to_create: this.buildConfirmPayload(),
        });
        this.$notification.success(response.data.message || "Subject import completed successfully.", {
          timer: 3,
          position: "bottomCenter",
        });
        this.showList();
      } catch (error) {
        this.handleConfirmBackendError(error, "Subject import save failed.");
      } finally {
        this.isCommitting = false;
        this.isLoading = false;
      }
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row, index) => ({
        row_number: row.row_number || (index + 1),
        campus_grade_id: row.campus_grade_id,
        grade_name: String(row.grade_name || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        description: String(row.description || "").trim(),
      }));
    },

    buildConfirmPayload() {
      return this.rowsNormalized.map((row, index) => ({
        row_number: row.row_number || (index + 1),
        grade_name: String(row.grade_name || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        description: String(row.description || "").trim(),
      }));
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const seenSubjects = {};

      this.rowsNormalized.forEach((row, index) => {
        const rowErrors = {};
        const gradeName = String(row.grade_name || "").trim();
        const subjectName = String(row.subject_name || "").trim();
        const description = String(row.description || "").trim();

        if (!gradeName) {
          rowErrors.grade_name = "Grade Name is required.";
        }

        if (!subjectName) {
          rowErrors.subject_name = "Subject Name is required.";
        } else if (subjectName.length > 255) {
          rowErrors.subject_name = "Max 255 characters.";
        }

        if (description.length > 1000) {
          rowErrors.description = "Max 1000 characters.";
        }

        if (gradeName && subjectName && !rowErrors.subject_name) {
          const subjectKey = `${gradeName.toLowerCase()}|${subjectName.toLowerCase()}`;
          if (seenSubjects[subjectKey]) {
            rowErrors.subject_name = "Duplicate subject in preview.";
          } else {
            seenSubjects[subjectKey] = true;
          }
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    resetAll() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.template_acd_year_id = null;
      this.acdYearId = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.invalidFileSize = false;
      this.clearPreview();

      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }

      this.getParentAcdYearList();
    },

    applyPreviewResponse(data) {
      this.rowsNormalized = this.clonePreviewRows(data.rows_normalized || data.to_create || []);
      this.previewErrors = data.errors || [];
      this.clearImportConfirmErrors();
    },

    clearPreview() {
      this.rowsNormalized = [];
      this.previewErrors = [];
      this.clearImportConfirmErrors();
    },

    handleAcademicYearChange(newValue, oldValue) {
      if (oldValue !== null && newValue !== oldValue && this.previewVisible) {
        this.clearPreview();
      }
    },

    handleImportValidationError(error, fallbackMessage) {
      const data = error.response?.data || {};
      const status = error.response?.status;
      const errors = data.errors || (data.msg ? [data.msg] : []);
      const messages = Array.isArray(errors) ? errors : Object.values(errors).flat();
      const message = messages[0] || data.message || fallbackMessage;

      if (status === 422) {
        window.alert(messages.join("\n") || message);
        this.clearPreview();
        return;
      }

      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return {
        rowKey: "row_number",
        payloadPrefixes: ["to_create"],
      };
    },

    handleImportError(error, fallbackMessage) {
      this.handleImportValidationError(error, fallbackMessage);
    },

    showList() {
      this.$router.push({ name: "subjectList" });
    },

    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
      } else {
        console.error(error);
      }
    },
  },

  mounted() {
    this.getParentAcdYearList();
  },

  watch: {
    acdYearId(newValue, oldValue) {
      this.handleAcademicYearChange(newValue, oldValue);
    },
  },
};
